import ProductsListing from 'components/pages/productsListing';
import { fetchContent } from 'lib/contentful/api';
import {
  IMAGE_QUERY_FIELDS,
  LINK_QUERY_FIELDS,
  RICHTEXT_QUERY_FIELDS,
  pageSectionsCollectionFragment,
} from 'lib/contentful/fragments';
import { SEO_FRAGMENT } from 'components/SEO';
import getLayoutItems from 'lib/contentful/getLayoutItems';
import withStoreLocales from 'lib/storeLocales/staticPaths/withStoreLocales';
import withStoreLocale from 'lib/storeLocales/staticProps/withStoreLocale';
import getCurrency from 'lib/storeLocales/getCurrency';
import { asyncPipe } from 'lib/utils/helpers';
import getStoreLocaleSlugs from 'lib/storeLocales/staticProps/getStoreLocaleSlugs';
import getLocaleSlugs from 'lib/storeLocales/staticProps/parseLocaleSlugs';
import addSections from 'lib/contentful/addSections';
import { collectionPageFetcher } from 'components/pages/productsListing/productsListing';
import fetchLocaleSlugs from 'lib/storeLocales/staticPaths/fetchLocaleSlugs';
import createPaths from 'lib/storeLocales/staticPaths/createPaths';
import revalidate from 'lib/revalidate';
import { getResourceText } from 'lib/resources';
import getMenu from 'lib/shopify/getMenu';

export default ProductsListing;

const getProductsListingPageQuery = ({ slug, locale = 'en', preview }) => `
  {
    pageProductsListingCollection(limit: 1, where: { slug: "${slug}" }, locale: "${locale}", preview: ${preview}) {
      items {
        sys { id }
        slug
        shopifyHandle
        highstreetShopifyId
        title
        pageTitle
        description {
          ${RICHTEXT_QUERY_FIELDS}
        }
        gridLayout
        desktopGridLayout
        navigation {
          resourcesCollection(limit: 20) {
            items {
              ... on ComponentLink {
                ${LINK_QUERY_FIELDS}
              }
            }
          }
        }
        breadcrumbsCollection {
          items {
            ... on ComponentLink {
              ${LINK_QUERY_FIELDS}
            }
          }
        }
        gridBannersCollection {
          items {
            ... on ComponentGridBanner {
              __typename
              name
              index
              indexDesktop
              image {
                ${IMAGE_QUERY_FIELDS}
              }
              videoUrl
              title
              link {
                ${LINK_QUERY_FIELDS}
              }
            }
          }
        }
        ${pageSectionsCollectionFragment(['SectionBanners'])}
        ${SEO_FRAGMENT}
      }
    }
  }
`;

const getProductListingMicrocopiesQuery = ({ locale = 'en', preview }) => `
  {
    globalModuleCollection(limit: 1, where: { name: "PLP Microcopies" }, locale: "${locale}", preview: ${preview}) {
      items {
        resourcesCollection(limit: 200) {
          items {
            ... on ComponentMicrocopy {
              name
              text
            }
          }
        }
      }
    }
  }
`;

const getProductListingColorsQuery = ({ preview }) => `
  {
    globalModuleCollection(limit: 1, where: { name: "HEX Colors" }, preview: ${preview}) {
      items {
        table
      }
    }
  }
`;

const pluckItem = page => page?.pageProductsListingCollection?.items[0];
const pluckResources = data => data?.globalModuleCollection?.items?.[0]?.resourcesCollection?.items;
const pluckTable = data => data?.globalModuleCollection?.items?.[0]?.table?.tableData;

export const getStaticProps = withStoreLocale(
  async ({ params, preview = false, previewData = null, locale, store }) => {
    const { slug } = params;

    const currency = getCurrency(store);

    const enrichPage = asyncPipe(addSections(locale, preview));
    const corePage = pluckItem(
      await fetchContent(getProductsListingPageQuery({ slug, locale, preview }), preview)
    );

    if (!corePage) return { notFound: true };

    const [page, microcopies, colors] = await Promise.all([
      enrichPage(corePage),
      await pluckResources(
        await fetchContent(getProductListingMicrocopiesQuery({ locale, preview }), preview)
      ),
      await pluckTable(await fetchContent(getProductListingColorsQuery({ preview }), preview)),
    ]);

    if (!page?.shopifyHandle) return { notFound: true };

    const initialCollection = await collectionPageFetcher(store)(page.shopifyHandle);

    if (!initialCollection) return { notFound: true };

    const appMenuHandle = getResourceText(microcopies)('app - menu handle', 'app-menu');
    const appMenu = await getMenu(store)(appMenuHandle);
    const appMenuItemId = (appMenu?.items || [])
      .reduce((acc, { items, ...restCurr }) => [...acc, restCurr, ...items], [])
      .find(({ resourceId }) => resourceId === initialCollection.id)?.id;
    const appCategoryId = appMenuItemId ? Buffer.from(appMenuItemId).toString('base64') : null;

    const storeLocaleSlugs = await getStoreLocaleSlugs(
      page.sys.id,
      'pageProductsListing',
      'collection',
      preview
    );
    const localeSlugs = getLocaleSlugs(store, storeLocaleSlugs);

    const { header, footer, cart, storeLocaleSelector, newsletterPopup, uspBanner } =
      await getLayoutItems(locale, preview, store);

    return {
      props: {
        previewData,
        page: {
          ...page,
          appCategoryId,
          seo: {
            ...(page.seo || {}),
            localeSlugs,
          },
        },
        header,
        footer,
        cart,
        microcopies,
        colors,
        currency,
        locale,
        store,
        initialCollection,
        newsletterPopup,
        uspBanner,
        storeLocaleSelector: {
          ...storeLocaleSelector,
          storeLocaleSlugs,
        },
      },
      revalidate,
    };
  }
);

/**
 * TODO: Keep below for eventual implementation of SSG
 */

const query = `
query pages($locale: String) {
  pageProductsListingCollection(where: { slug_exists: true }, locale: $locale, limit:1000, preview: false) {
    items {
      slug
      shopifyHandle
    }
  }
}
`;

/**
 * @returns {Object} { 'eu': ['handle1'], 'uk': ['handle3', 'handle4'] }
 */
// const getHandlesPerStore = (storeLocales, localePages) => {
//   const localesPerStore = stackEntries(storeLocales.map(split));

//   return Object.entries(localesPerStore).reduce(
//     (storeHandles, [store, locales]) => ({
//       ...storeHandles,
//       [store]: distinct(
//         locales.map(locale => localePages[locale].map(({ shopifyHandle }) => shopifyHandle)).flat()
//       ),
//     }),
//     {}
//   );
// };
export const getAllPaths = withStoreLocales(async ({ locales, storeLocales }) => {
  const localePages = await fetchLocaleSlugs(locales, query);
  const paths = createPaths(storeLocales, localePages);

  return {
    paths,
  };
});

export const getStaticPaths = withStoreLocales(async ({ storeLocales }) => {
  // const localePages = await fetchLocaleSlugs(locales, query);
  // const paths = createPaths(storeLocales, localePages);

  // if (isBuilding) {
  //   const handlesPerStore = getHandlesPerStore(storeLocales, localePages);
  //   await cacheShopifyCollections(handlesPerStore);
  // }

  return {
    paths: createPaths(['eu-en'], { en: [{ slug: 'all' }] }), // Static build for accurate lighthouse audit
    fallback: 'blocking',
  };
});
